/**
 * MachineSelectCell.tsx
 * Dropdown cell for selecting a capable machine within a TaskTableRow.
 *
 * Each MachineBooking row gets its own dropdown (filtered to TaskType capabilities).
 * Phase 2, Item 3.
 *
 * Follow-up 1.12: when showNoCapableWarning=true and the capable-machine list
 * comes back empty, the dropdown is replaced by an orange warning box with a
 * link to /schedule/machines so the user knows what to do.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Typography,
  Box,
  Tooltip,
  Button,
} from '@mui/material';
// Use specific subpath import to avoid EMFILE from the @mui/icons-material barrel.
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import type { SelectChangeEvent } from '@mui/material/Select';
import { listCapableMachines, type CapableMachine } from '../../../services/scheduleTasks';

interface Props {
  taskTypeKey: string | null;
  /** The task type display name — used in the no-capable-machine warning message. */
  taskTypeName?: string | null;
  currentMachineKey: string;
  bookingKey: string;
  disabled?: boolean;
  onChangeMachine: (bookingKey: string, newMachineKey: string) => Promise<void>;
  /**
   * When true and the capable-machine list is empty after loading, this cell
   * renders a warning message instead of an empty dropdown.
   *
   * Pass true only for the initial-place scenario (no existing bookings on the
   * task). Leave false/undefined for existing-booking rows where the current
   * machine should always be shown regardless of capability.
   */
  showNoCapableWarning?: boolean;
  /**
   * Optional callback fired after the capable-machine list loads.
   * Receives the count so the parent can react (e.g. highlight the row).
   */
  onCapableMachineCountChanged?: (count: number) => void;
}

export const MachineSelectCell: React.FC<Props> = ({
  taskTypeKey,
  taskTypeName,
  currentMachineKey,
  bookingKey,
  disabled = false,
  onChangeMachine,
  showNoCapableWarning = false,
  onCapableMachineCountChanged,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [machines, setMachines] = useState<CapableMachine[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadMachines = useCallback(async () => {
    if (!taskTypeKey) return;
    setLoading(true);
    try {
      const result = await listCapableMachines(taskTypeKey);
      setMachines(result);
      onCapableMachineCountChanged?.(result.length);
    } catch {
      // Silently fail — user sees an empty dropdown (or the warning if showNoCapableWarning)
      setMachines([]);
      onCapableMachineCountChanged?.(0);
    } finally {
      setLoading(false);
    }
  }, [taskTypeKey, onCapableMachineCountChanged]);

  useEffect(() => {
    loadMachines();
  }, [loadMachines]);

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const newKey = event.target.value;
    if (newKey === currentMachineKey) return;
    setSaving(true);
    try {
      await onChangeMachine(bookingKey, newKey);
    } finally {
      setSaving(false);
    }
  };

  if (!taskTypeKey) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
        No task type
      </Typography>
    );
  }

  if (loading) {
    return <CircularProgress size={16} />;
  }

  // ── No capable machines warning (initial-place scenario only) ──────────────
  // Only shown when the caller opts in AND no capable machines were found AND
  // no machine is already assigned (currentMachineKey is empty).
  if (showNoCapableWarning && machines.length === 0 && !currentMachineKey) {
    const typeLabel = taskTypeName ?? 'this task type';
    return (
      <Tooltip
        title="Open the Machines admin to grant a machine this capability, then come back and refresh."
        arrow
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            border: `1px solid ${theme.schedule.conflictSoft}`,
            backgroundColor: `${theme.schedule.conflictSoft}14`, // ~8% opacity tint
            maxWidth: 280,
          }}
          role="alert"
          aria-label={`No active machine has the ${typeLabel} capability yet`}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningAmberOutlined
              sx={{ fontSize: 15, color: theme.schedule.conflictSoft, flexShrink: 0 }}
            />
            <Typography
              variant="caption"
              sx={{ color: theme.schedule.conflictSoft, fontWeight: 500, lineHeight: 1.3 }}
            >
              No active machine has the <em>{typeLabel}</em> capability yet
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={() => navigate('/schedule/machines')}
            sx={{
              alignSelf: 'flex-start',
              fontSize: '0.7rem',
              color: theme.schedule.conflictSoft,
              px: 0,
              minWidth: 0,
              textTransform: 'none',
              '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
            }}
            data-testid="no-capable-machine-add-btn"
          >
            Add one in Machines
          </Button>
        </Box>
      </Tooltip>
    );
  }

  // ── Normal dropdown ────────────────────────────────────────────────────────
  // Always include the current machine even if not in the capable list
  const options = [...machines];
  const currentInList = machines.some((m) => m.MachineKey === currentMachineKey);
  if (!currentInList && currentMachineKey) {
    // Add a placeholder for the currently assigned (possibly incapable) machine
    options.unshift({ MachineKey: currentMachineKey, Code: '?', Name: '(current)', LabSection: null });
  }

  return (
    <FormControl size="small" fullWidth disabled={disabled || saving}>
      <Select
        value={currentMachineKey}
        onChange={handleChange}
        displayEmpty
        sx={{ fontSize: '0.78rem', minWidth: 140 }}
        renderValue={(val: string) => {
          const found = machines.find((m) => m.MachineKey === val);
          if (found) return `${found.Code}: ${found.Name}`;
          if (val === currentMachineKey) return saving ? 'Saving…' : '(current)';
          return val;
        }}
      >
        {options.map((m) => (
          <MenuItem key={m.MachineKey} value={m.MachineKey} sx={{ fontSize: '0.78rem' }}>
            {m.Code}: {m.Name}
            {m.LabSection && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                [{m.LabSection}]
              </Typography>
            )}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
